/**
 * Phase 7G.5 — Admin dark mode foundation.
 *
 * Applies `[data-admin-theme="light|dark"]` on <html>, persisted in
 * localStorage under `lwp_admin_theme`. CSS variables are declared in
 * /app/frontend/src/App.css. This module only owns the toggle + hook.
 *
 * Public marketing pages ignore the attribute (they hardcode `#FAF8F3`).
 */
import React, { useEffect, useState, useCallback } from 'react'

const STORAGE_KEY = 'lwp_admin_theme'

function readTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark') return v
  } catch (_) {
    /* ignore */
  }
  return 'light'
}

export function useAdminTheme() {
  const [theme, setTheme] = useState(readTheme)

  // Apply on mount + whenever theme changes
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute('data-admin-theme', theme)
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch (_) {
      /* ignore */
    }
  }, [theme])

  // Clear on unmount — public pages should render in light mode
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-admin-theme', 'light')
      }
    }
  }, [])

  const toggle = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'))
  }, [])

  return { theme, setTheme, toggle }
}

export function AdminThemeToggle() {
  const { theme, toggle } = useAdminTheme()
  const dark = theme === 'dark'
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="admin-theme-toggle"
      aria-label={`Switch to ${dark ? 'light' : 'dark'} mode`}
      title={`Switch to ${dark ? 'light' : 'dark'} mode`}
      style={{
        padding: '6px 12px',
        background: 'transparent',
        color: 'var(--admin-slate)',
        border: '1px solid var(--admin-border)',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span style={{ fontSize: 13 }} aria-hidden>
        {dark ? '☀' : '☾'}
      </span>
      {dark ? 'Light' : 'Dark'}
    </button>
  )
}
